import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types';
import { useService } from '@/scopes/checkout/domain/hooks';
import type { SheetVariant } from '@sunwebgroup/ui-components/src';
import * as React from 'react';
import {
  Icon,
  ListItem,
  Sheet,
  SheetContent,
  SheetOverlay,
  SheetPortal, space, SpaceBetween, tok, Typography,
} from '@sunwebgroup/ui-components';
import { createCarEvent } from '@/scopes/checkout/domain/analytics';
import { useCarRentalServiceStore } from '@/scopes/checkout/components/services/CarRentalFilteringService/store';
import { useState } from 'react';
import { styled } from 'styled-components';
import { useIntl } from '@/intl';
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import {
  EquipmentsForm
} from '@/scopes/checkout/components/services/CarRentalFilteringService/Equipments/EquipmentsForm';
import { prop } from 'ramda';
import {
  getTotalPriceForEquipmentsSelected
} from '@/scopes/checkout/components/services/CarRentalFilteringService/selectors';

export function EquipmentsSheet({openByDefault= false, ...option }: CheckoutCarRentalServiceOption & { openByDefault?: boolean }) {
  const { service } = useService(ServiceType.CarRentalFiltering);
  const intl = useIntl();
  const { isDesktopSm } = useBreakpoints();
  const driverFormWrapperSide = isDesktopSm ? 'right' : 'bottom' as SheetVariant
  const {sendEvent} = useAnalytics();
  const {startEditingCar, endEditingCar,
  } = useCarRentalServiceStore()
  const [showEquipmentsForm, setShowEquipmentsForm] = useState(false);
  const {participants } = service || {};
  const {driverId, value: carValue, id: carId, selectedEquipmentsWithQuantity, equipments} = option;

  const driver = driverId ? participants?.[driverId] : null;
  React.useEffect(() => {
    if (openByDefault) {
      setTimeout(() => {
        setShowEquipmentsForm(true);
      }, 300);
    }
  }, [setShowEquipmentsForm, openByDefault])

  const handleClickEquipmentsForm = () => {
    setShowEquipmentsForm(true);
    startEditingCar(carId, {
      driverId: driverId ?? '',
      driverBirthDate: driver?.birthdate ? new Date(driver.birthdate) : new Date(),
      driverHasCreditCard: Boolean(driverId && driverId !== '0'),
      bookedEquipmentsWithQuantity: option.selectedEquipmentsWithQuantity?.map(({id, quantity}) => {
        return {
          id,
          quantity
        };
      }) ?? [],
      isEditing: Boolean(driverId && driverId !== '0')
    })
    sendEvent(
      createCarEvent({
        result: 'edit',
        type: carValue,
        description: 'add equipments button clicked',
        element: 'button',
      })
    )
  }

  const handleEndEditing = () => {
    endEditingCar(option.value);
    setShowEquipmentsForm(false);
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleClickEquipmentsForm();
    }
  }

  if (!service) return null;
  if (!option) return null;
  const selectedEquipmentsList = equipments.filter(({ id }) => selectedEquipmentsWithQuantity?.map(({id: selectedEquipmentId}) => selectedEquipmentId ).includes(id));
  const totalEquipmentPrice = getTotalPriceForEquipmentsSelected(option);

  return <>
    <EquipmentsSheetTrigger onClick={handleClickEquipmentsForm} onKeyPressCapture={handleKeyDown}>
      <ListItem.Content>
        <EquipmentsCategoryText>{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_overline'})}</EquipmentsCategoryText>
        <ListItem.Label>{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_action_title'})}</ListItem.Label>
        <EquipmentsHintText>
          { (!selectedEquipmentsList || selectedEquipmentsWithQuantity?.length === 0) && intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_paid_locally'})}
          { (selectedEquipmentsList.length > 0) ? (
            <CarSelectedEquipments equipments={selectedEquipmentsList} />
          ) : null
          }
        </EquipmentsHintText>

      </ListItem.Content>
      <ListItem.Trailing>
        <SpaceBetween direction="x" spacing={space('primitives_space_8')}>
          { (selectedEquipmentsList.length > 0 && totalEquipmentPrice && totalEquipmentPrice > 0) && (
            <Typography>{intl.formatCurrency(totalEquipmentPrice)}</Typography>
            )}
          <Icon glyph="IconChevronRight" size="md"/>
        </SpaceBetween>
      </ListItem.Trailing>
    </EquipmentsSheetTrigger>
    <Sheet modal open={showEquipmentsForm}>
      <SheetPortal>
        <SheetOverlay onClick={handleEndEditing} />
        <SheetContent $side={driverFormWrapperSide} onEscapeKeyDown={handleEndEditing}>
          <EquipmentsForm {...option} showEquipmentsForm={showEquipmentsForm} setShowEquipmentsForm={setShowEquipmentsForm}/>
        </SheetContent>
      </SheetPortal>
    </Sheet>
  </>
}
function CarSelectedEquipments ({equipments}: {equipments: {id: string; text: string}[]}) {
  const intl = useIntl();
  if (!equipments.length) return null;
  return (
    <Typography $variant="label">
      {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_selected_extras' })}
      <Extras $variant="body-sm">
        {equipments.map(({ text }) => text).join(', ')}
      </Extras>
    </Typography>
  )
}

const Extras = styled(Typography)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`

const EquipmentsSheetTrigger = styled(ListItem.Root)`
  background-color: ${tok('semantics_backgrounds_tinted_background_color')};
  border-color: ${tok('semantics_backgrounds_tinted_background_color')};
  color: ${tok('semantics_text_color')};
  padding: ${space('primitives_space_12')};
`
const EquipmentsCategoryText = styled(ListItem.Description)`
  color: ${tok('semantics_text_color')};
`
const EquipmentsHintText = styled(ListItem.Description)`
  color: ${tok('semantics_text_secondary_color')};;
`

